import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { DEFAULT_GOAL_COLLECTION_EFFORT, GOAL_COLLECTION_OPTIONS } from "@/lib/agent/goalCollection";
import type { Agent, GoalCollectionEffort } from "@/types/agent/agent";

export const GoalCollectionEffortField = ({ agent }: { agent: Agent }) => {
  const savedEffort = agent.settings?.goal_collection_effort ?? DEFAULT_GOAL_COLLECTION_EFFORT;
  const [effort, setEffort] = useState<GoalCollectionEffort>(savedEffort);
  const updateAgent = useUpdateAgent();

  useEffect(() => {
    setEffort(savedEffort);
  }, [savedEffort]);

  const handleChange = async (next: GoalCollectionEffort) => {
    setEffort(next);
    try {
      await updateAgent.mutateAsync({ agent, updateData: { settings: { goal_collection_effort: next } } });
      toast.success("Saved. This applies from the next conversation.");
    } catch (error) {
      console.error("Failed to save goal collection effort:", error);
      setEffort(savedEffort);
      toast.error("Couldn't save that. Please try again.");
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="text-sm font-medium text-gray-900">How hard your genie asks for these details</div>
      <p className="text-xs text-gray-500 mt-0.5">
        Details people mention are always captured after the conversation.
      </p>

      <div className="mt-3 space-y-2" role="radiogroup" aria-label="How hard your genie asks for these details">
        {GOAL_COLLECTION_OPTIONS.map((option) => {
          const isSelected = effort === option.effort;
          return (
            <label
              key={option.effort}
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                isSelected
                  ? "bg-genie-primary-light/60 border-genie-primary/40"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name={`goal-collection-effort-${agent.id}`}
                checked={isSelected}
                disabled={updateAgent.isPending}
                onChange={() => handleChange(option.effort)}
                className="mt-0.5 border-gray-300 text-genie-primary focus:ring-genie-primary"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
